import React from 'react';

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 * LazyImage
 * - Defers setting `src` until the image is near the viewport (IntersectionObserver)
 * - Optional `delayMs` to delay even after it becomes visible
 *
 * Notes:
 * - For the very first above-the-fold hero image, delaying may cause a blank flash.
 */
export default function LazyImage({
  src,
  alt = '',
  delayMs = 0,
  rootMargin = '300px',
  threshold = 0.01,
  className,
  style,
  width,
  height,
  loading, // allow overriding if you want "eager"
  decoding = 'async',
  ...rest
}) {
  const imgRef = React.useRef(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);

  React.useEffect(() => {
    // If no src provided, don't do anything.
    if (!src) return;

    // Fallback: if IntersectionObserver isn't supported, load immediately.
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const el = imgRef.current;
    if (!el) return;

    let timeoutId;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        observer.disconnect();

        if (delayMs > 0) {
          timeoutId = window.setTimeout(() => setShouldLoad(true), delayMs);
        } else {
          setShouldLoad(true);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [src, delayMs, rootMargin, threshold]);

  return (
    <img
      ref={imgRef}
      src={shouldLoad ? src : TRANSPARENT_PIXEL}
      data-src={src}
      alt={alt}
      className={className}
      style={style}
      width={width}
      height={height}
      loading={loading ?? 'lazy'}
      decoding={decoding}
      {...rest}
    />
  );
}

